
import React, { useState, useEffect } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { useAuth } from '@/contexts/auth/AuthContext';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Card, CardContent, CardDescription, CardFooter, CardHeader, CardTitle } from '@/components/ui/card';
import { Label } from '@/components/ui/label';
import { LogIn, Loader2, User } from 'lucide-react';
import { Alert, AlertDescription } from '@/components/ui/alert';
import Navbar from '@/components/Navbar';
import Gradient from '@/assets/medihub_gradient_mesh.jpg';
import Footer from '@/components/Footer';

const Login = () => {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const { login, isAuthenticated } = useAuth();
  const navigate = useNavigate();

  // Redirect if already authenticated
  useEffect(() => {
    if (isAuthenticated) {
      navigate('/');
    }
  }, [isAuthenticated, navigate]);

  // Reset error message when inputs change
  useEffect(() => {
    if (errorMessage) {
      setErrorMessage(null);
    }
  }, [email, password]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (isSubmitting) {
      console.log('Form submission already in progress, ignoring click');
      return;
    }

    setIsSubmitting(true);
    setErrorMessage(null);

    try {
      const success = await login(email, password);
      if (!success) {
        setErrorMessage("Bejelentkezési hiba. Kérjük, ellenőrizze adatait és próbálja újra.");
      }
      // Navigation is handled inside login function
    } catch (error) {
      console.error('Login error:', error);
      setErrorMessage("Váratlan hiba történt. Kérjük, próbálja újra később.");
    } finally {
      // Késleltetett állapotváltás a többszöri kattintás elkerülésére
      setTimeout(() => {
        setIsSubmitting(false);
      }, 500);
    }
  };

  const handleTestLogin = async (e: React.MouseEvent) => {
    e.preventDefault();

    if (isSubmitting) {
      return;
    }

    setIsSubmitting(true);
    setErrorMessage(null);

    try {
      await login('test@example.com', 'test123');
      // Navigation is handled inside login function
    } catch (error) {
      console.error('Test login error:', error);
      setErrorMessage("Váratlan hiba történt a teszt bejelentkezésnél.");
    } finally {
      setTimeout(() => {
        setIsSubmitting(false);
      }, 500);
    }
  };

  const handleParticipantLogin = async (e: React.MouseEvent) => {
    e.preventDefault();

    if (isSubmitting) {
      return;
    }

    setIsSubmitting(true);
    setErrorMessage(null);

    try {
      await login('participant@example.com', 'test123');
      // Navigation is handled inside login function
    } catch (error) {
      console.error('Participant login error:', error);
      setErrorMessage("Váratlan hiba történt a résztvevői bejelentkezésnél.");
    } finally {
      setTimeout(() => {
        setIsSubmitting(false);
      }, 500);
    }
  };

  return (
    <div className="flex min-h-screen flex-col relative">
      <Navbar />

      <div className="flex-1 py-6 pt-32 relative z-10">

        <div className="absolute inset-0 z-0">
          <img
            src={Gradient}
            alt="MediHub háttér"
            className="w-full h-full object-cover"
          />
          <div className="absolute inset-0 bg-black/10 z-0"></div>
        </div>

        <div className="container max-w-xl mx-auto px-4 relative z-10">
          <Card >
            <CardHeader className="space-y-1">
              <CardTitle className="text-2xl font-bold text-center">Bejelentkezés</CardTitle>
              <CardDescription className="text-center">
                Jelentkezzen be fiókjába a folytatáshoz
              </CardDescription>
            </CardHeader>
            <form onSubmit={handleSubmit}>
              <CardContent className="space-y-4">
                {errorMessage && (
                  <Alert variant="destructive">
                    <AlertDescription>{errorMessage}</AlertDescription>
                  </Alert>
                )}

                <div className="space-y-2">
                  <Label htmlFor="email">Email cím</Label>
                  <Input
                    id="email"
                    type="email"
                    placeholder="email@example.com"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    required
                    disabled={isSubmitting}
                  />
                </div>
                <div className="space-y-2">
                  <div className="flex items-center justify-between">
                    <Label htmlFor="password">Jelszó</Label>
                    <Link
                      to="/forgot-password"
                      className="text-sm text-medihub-primary hover:underline"
                      tabIndex={isSubmitting ? -1 : 0}
                    >
                      Elfelejtette jelszavát?
                    </Link>
                  </div>
                  <Input
                    id="password"
                    showPasswordToggle={true}
                    type="password"
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    required
                    disabled={isSubmitting}
                  />
                </div>
              </CardContent>
              <CardFooter className="flex flex-col space-y-4">
                <Button
                  type="submit"
                  className="w-full"
                  disabled={isSubmitting}
                >
                  {isSubmitting ? (
                    <>
                      <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                      Bejelentkezés...
                    </>
                  ) : (
                    <>
                      <LogIn className="mr-2 h-4 w-4" />
                      Bejelentkezés
                    </>
                  )}
                </Button>


                <div className="text-center text-sm">
                  Nincs még fiókja?{" "}
                  <Link
                    to="/register/participant"
                    className="text-medihub-primary hover:underline"
                    tabIndex={isSubmitting ? -1 : 0}
                  >
                    Regisztráljon!
                  </Link>
                </div>
              </CardFooter>
            </form>
          </Card>
        </div>

      </div>
      <Footer />
    </div>
  );
};

export default Login;
